import React from 'react';
import { Phone } from 'lucide-react';

const FiveDaysSafari = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">5 DAYS LAKE MANYARA & SERENGETI WILDLIFE SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          5 Days Lake Manyara & Serengeti Wildlife Safari
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Experience the ultimate in Tanzania safaris with our <strong>5 Days Lake Manyara & Serengeti Wildlife Safari</strong> through Tanzania National Park and Serengeti National Park. This exclusive 5-day Tanzania safari package offers unparalleled game viewing and unique activities such as a Hot Air Balloon Safari, all set against the stunning backdrop of Tanzania's most iconic landscapes. Enjoy stays in luxurious lodges situated in prime locations.
              </p>
            </div>

            {/* Placeholder for Flamingo Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/flamingo2.JPG" 
                alt="3 Days Tanzania Wildlife Safari" 
                className="w-full h-48 object-cover"
              />
                
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Flamingo Birds by the shores of Lake Manyara
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 5 Days Lake Manyara & Serengeti Wildlife Safari
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Transfer to Lake Manyara National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Your private guide will meet you at your hotel in Arusha for an early departure to Lake Manyara National Park. You will travel in a private 4x4 Landcruiser safari vehicle to your reserved lodge for a relaxing lunch. Later embark on an afternoon game drive, offering the chance to observe wildlife including Lions, Leopards, Cheetahs, Rhinoceros, Elephants, and Wildebeests. Your driver guide will drive you to not just the Big Five but a variety of animal species. Later return to the lodge for dinner and overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Lake Manyara Serena Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Full Day Game Drive in Lake Manyara National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Today you will enjoy adventurous game drives in Lake Manyara National Park. Early mornings and late afternoons are ideal for spotting active wildlife. As the safari is private, you can adjust the schedule to suit your preferences. Alternatively, opt for a full day with a picnic lunch. Later in the evening return to the lodge for dinner and overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Lake Manyara Serena Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Transfer to Serengeti National Park and Game Drive</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After a relaxed breakfast transfer to Serengeti National Park, arriving at your lodge in the early afternoon for check-in and lunch. After lunch embark on the afternoon game drive, allowing exploration of the park's diverse ecosystem. Later in the evening return to the lodge for dinner and overnight stay.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Serengeti Serena Safari Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 4: Full Day Game Drive in Serengeti National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Today is dedicated to exploring the Serengeti's remarkable wildlife. Early morning game drives and late afternoon drives are ideal for spotting predators like lions, cheetahs, and leopards. This immersion in nature promises to be a transformative experience. For a full day, opt to embark on a morning and late afternoon game drive, returning to the camp for lunch. Alternatively, opt for a full day with a picnic lunch.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Serengeti Serena Safari Lodge
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 5: Transfer Back to Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  At dawn, embark on a final game drive in the Serengeti followed by a leisurely breakfast. Check and enjoy a scenic drive back to Arusha. Arrive in Arusha by late afternoon for drop-off at your hotel or transfer to the airport for your departure flight.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Transportation in a 4x4 safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodations and meals stated
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Entrance fees to all National Parks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    English Driver guide
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Personal expenses
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Health test requirements
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Gratuities
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">5 Days Lake Manyara & Serengeti Wildlife Safari</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li>Duration: 5 Days, 4 Nights safari</li>
                  <li>Departures / Returns: Hotel pick-up. You are returned to your hotel.</li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiveDaysSafari;